/* eslint-disable no-plusplus */
import React from 'react';
import Datepicker from 'react-datepicker';
import { Field } from 'react-form';
import PropTypes from 'prop-types';
import moment from 'moment';

import getOptionsForTimeSelect from '../utils';
import { defaultDateFormat } from '../../../utils/eventConstants';

import styles from './DateTimePicker.scss';

const timeInterval = 30;

function DateTimePickerInput(props) {
  return (
      <Field field={props.field} validate={props.validate} minDate={props.minDate} maxDate={props.maxDate} selected={props.selected}>
          {fieldApi => {
            const {
                id,
                label,
                field,
                validate,
                hiddenLabel,
                description,
                handleDateChange,
                selected,
                minDate,
                maxDate,
                hoverText,
                dateFormat,
                timeLabel = 'Time',
                dateLabel = 'Date',
                customClassName,
                hideTimePicker = false,
                defaultDate,
                disabled,
                disableDateSelector,
                isStacked,
                ...rest
            } = props;

            const { error, warning, touched, setValue, setTouched, onBlur } = fieldApi;
            const options = getOptionsForTimeSelect();
            let updatedTime;

            if (selected) {
                const time = selected?.format('HH:mm');
                const isValidTimeOption = options.some(timeOption => timeOption.value === time);

                if (isValidTimeOption) {
                    updatedTime = time;
                } else {
                    const minuteRemainder = timeInterval - (selected.minute() % timeInterval);
                    const roundedTime = selected.add(minuteRemainder, 'minutes');
                    updatedTime = roundedTime.format('HH:mm');
                }
            } else {
                updatedTime = '';
            }

            function onDateChange(date) {
                setTouched();
                // update form state
                setValue(date);
                // update parent state (display)
                if (handleDateChange) {
                    handleDateChange(date);
                }
            }

            function onTimeChange(e) {
                const timeValue = e.target.value.split(':');
                const date = (selected || moment(defaultDate)).clone();
                date.set({ hour: timeValue[0], minute: timeValue[1], second: 0 });
                let isValidTimeOption = true;
                if ((minDate && date.isBefore(minDate)) || (maxDate && date.isAfter(maxDate))) {
                    isValidTimeOption = false;
                }
                if (isValidTimeOption) {
                    setTouched();
                    // update form state
                    setValue(date);
                    // update parent state (display)
                    if (handleDateChange) {
                        handleDateChange(date);
                    }
                }
            }

            return (
                <div className={`${styles.dateTimePicker} form-group form-group-xl ${customClassName || ''} ${(touched && error) && 'is-invalid'} ${description && 'description'} ${styles.themeV3}`}>
                    {description && (<small>{description}</small>)}
                    <div className="row">
                        <div className={isStacked ? 'col-12' : (hideTimePicker ? 'col-12 pr4' : 'col-6 pr4')}>
                            <div className={`${styles.inputWrapper} input-wrapper`}>
                                <Datepicker
                                    className={`${styles.formControl} form-control no-readonly-background`}
                                    selected={selected}
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    onChange={onDateChange}
                                    readOnly={false}
                                    dateFormat={dateFormat}
                                    onBlur={e => {
                                        setTouched();
                                        if (onBlur) {
                                            onBlur(e);
                                        }
                                    }}
                                    disabled={disableDateSelector || disabled}
                                    {...rest}
                                />
                                {dateLabel && <label className={styles.label}>{dateLabel}</label>}
                                <div className="error-indicator" />
                            </div>
                        </div>
                        {!hideTimePicker && (
                            <div className={isStacked ? 'col-12 mt16' : 'col-6 pl0'}>
                                <div className={`${styles.inputWrapper} input-wrapper`}>
                                    <select disabled={props.disabled} id={id} className={`${styles.formControl} form-control time-select`} onChange={onTimeChange} value={updatedTime}>
                                        <option value="">Time</option>
                                        {options
                                            .filter(option => {
                                                // filter selectable time options based on minDate and maxDate
                                                if (!selected) {
                                                    return true;
                                                }
                                                const [hour, minute] = option.value.split(':').map(Number);
                                                const testDate = selected.clone().set({ hour, minute, second: 0 });
                                                return (!minDate || !testDate.isBefore(minDate)) && (!maxDate || !testDate.isAfter(maxDate));
                                            })
                                            .map(option => (
                                                <option value={option.value} key={option.value}>{option.title}</option>
                                            ))}
                                    </select>
                                    {timeLabel && <label className={styles.label}>{timeLabel}</label>}
                                </div>
                            </div>
                        )}
                    </div>
                    {touched &&
                        ((error && <div className={styles.error}>{error}</div>) ||
                        (warning && <div className={styles.error}>{warning}</div>))}
                </div>
            );
        }}
      </Field>
  );
}

DateTimePickerInput.propTypes = {
    id: PropTypes.string,
    label: PropTypes.string,
    field: PropTypes.string.isRequired,
    validate: PropTypes.func,
    hiddenLabel: PropTypes.bool,
    description: PropTypes.string,
    handleDateChange: PropTypes.func,
    selected: PropTypes.instanceOf(moment).isRequired,
    minDate: PropTypes.instanceOf(moment).isRequired,
    maxDate: PropTypes.instanceOf(moment).isRequired,
    hoverText: PropTypes.string,
    dateFormat: PropTypes.string,
    themeV3: PropTypes.bool,
    timeLabel: PropTypes.string,
    dateLabel: PropTypes.string,
    customClassName: PropTypes.string,
    hideTimePicker: PropTypes.bool,
    disabled: PropTypes.bool,
    disableDateSelector: PropTypes.bool,
};

DateTimePickerInput.defaultProps = {
    id: '',
    validate: () => { },
    handleDateChange: () => { },
    label: '',
    description: '',
    hiddenLabel: false,
    hoverText: '',
    timeLabel: 'Time',
    dateLabel: 'Date',
    hideTimePicker: false,
    disabled: false,
    disableDateSelector: false,
    themeV3: false,
    customClassName: '',
    dateFormat: defaultDateFormat,
};

export default DateTimePickerInput;
